/**
 * src/pages/SubscriptionPage2.jsx — 2026-03-26
 * ------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------
 * Safe, parallel subscription/pricing page shell for the new
 * WordPress-aligned pricing UI.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------
 * We do NOT want to modify the current SubscriptionPage.jsx yet
 * because:
 *   • the existing production billing/subscription flow works
 *   • the new pricing renderer should be tested locally first
 *   • we want a clean side-by-side migration path
 *
 * This page keeps the existing provider structure:
 *   • <OrganizationProvider2>
 *   • <BillingsProvider>
 *
 * It also preserves the current app-side runtime/billing behavior:
 *   • current subscription state still comes from BillingContext
 *   • seat changes still flow through updateSeats()
 *   • review/confirm still happens in ReviewChangesDrawer
 *
 * WHAT CHANGES HERE
 * ------------------------------------------------------------
 * This page introduces a new page-level structure that is intended
 * to align with the WordPress pricing-page hierarchy:
 *
 *   1. page header / intro
 *   2. pricing cards
 *   3. compare section (to be added later)
 *   4. FAQ/footer sections (optional later)
 *
 * CURRENT SCOPE
 * ------------------------------------------------------------
 * For this stage, this page renders:
 *   • PricingPageHeader2
 *   • PricingTable2
 *   • ReviewChangesDrawer
 *
 * This file now also owns the shared pricing interval state so:
 *   • the header toggle can update it
 *   • the table can consume the same value
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * This page intentionally does NOT alter:
 *   • BillingContext.js
 *   • Stripe callable flows
 *   • ReviewChangesDrawer logic
 *   • current production SubscriptionPage.jsx
 */

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

import { Alert, Button, Box, Typography, Snackbar, Collapse } from "@mui/material";

import { useBillings } from "../contexts/BillingContext";

import PricingPageHeader2 from "../pricingtable/PricingPageHeader2";
import PricingTable2 from "../pricingtable/PricingTable2";
import PricingCompareTable2 from "../pricingtable/PricingCompareTable2";
import PricingFaq2 from "../pricingtable/PricingFaq2";
import ReviewChangesDrawer2 from "../pricingtable/ReviewChangesDrawer2";
import usePricingPresentation from "../hooks/usePricingPresentation";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/700.css";

/* ----------------------------------------------------------------
 * Session keys for Stripe-return staged-state recovery
 *
 * Why:
 * - Refs are lost on full-page navigation away to Stripe-hosted pages.
 * - We persist only the minimal staged billing launch snapshot needed to
 *   distinguish an abandoned checkout return from a genuinely settled change.
 * - sessionStorage is the correct scope because this state should survive the
 *   current tab's navigation flow, but should not leak across tabs/sessions.
 * ---------------------------------------------------------------- */
const STRIPE_RETURN_SNAPSHOT_KEY =
  "subscriptionPage2_stripeReturnSnapshot";
const STRIPE_RETURN_STAGED_KEY =
  "subscriptionPage2_hasStagedBillingChange";
const STRIPE_RETURN_RESET_DONE_KEY =
  "subscriptionPage2_hasHandledStripeReturnReset";

/* ──────────────────────────────────────────────────────────────
 * Child component inside providers
 * ─────────────────────────────────────────────────────────── */

const ManageSubscription2 = () => {
  const location = useLocation();
  const { state } = location;

const isStripeReturnContext =
  state?.from === "checkout-result" ||
  (
    typeof window !== "undefined" &&
    window.performance?.getEntriesByType?.("navigation")?.[0]?.type ===
      "back_forward"
  );

  const {
    currentSubscription,
    hasValidPaymentMethod,
    billingLoaded,
    redirectToCustomerPortal,
  } = useBillings();

  /**
   * Shared pricing presentation payload for the entire page.
   *
   * IMPORTANT:
   * - We load the pricing payload ONCE here at the page-shell level.
   * - Child components receive the same object via props.
   * - This prevents duplicate /pricing-data.json fetches from:
   *     • PricingPageHeader2
   *     • PricingTable2
   *     • future compare/FAQ/footer sections
   */
  const pricingPresentation = usePricingPresentation();

  /* ----------------------------------------------------------------
   * Local UI state
   * ---------------------------------------------------------------- */
  const [seatCount, setSeatCount] = useState(currentSubscription?.seats || 1);
  const [reviewContext, setReviewContext] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  /**
   * New shared pricing interval state for the page.
   *
   * Why:
   * - PricingPageHeader2 renders the WordPress-aligned annual/monthly toggle
   * - PricingTable2 must consume the same selected interval
   * - the page shell is the correct place to own this shared UI state
   *
   * Default:
   * - "annual" to match the current WordPress pricing page server-rendered state
   */
  const [pricingInterval, setPricingInterval] = useState("annual");

  /* ----------------------------------------------------------------
   * Enterprise billing-surface hydration guards
   *
   * Why:
   * - This page is an authenticated subscription-management surface, not a
   *   public pricing page.
   * - We should not render an interactive pricing UI before:
   *     1) pricingPresentation has loaded
   *     2) BillingContext has completed its first subscription snapshot
   *
   * IMPORTANT:
   * - The latest ReviewChangesDrawer2 intentionally supports BOTH:
   *     • settled existing subscriptions
   *     • brand-new / no-active-subscription flows
   * - So SubscriptionPage2 must NOT require currentSubscription.id/priceId/
   *   interval just to show the pricing surface.
   *
   * Enterprise rule:
   * - Hold the interactive pricing surface only until:
   *     1) pricingPresentation has loaded
   *     2) BillingContext has completed its first org billing hydration
   *
   * This still prevents:
   * - rendering from incomplete payload state
   * - premature interaction before BillingContext is ready
   *
   * But it no longer blocks:
   * - first-time subscription creation
   * - orgs with no settled subscription yet
   * ---------------------------------------------------------------- */
  const hasPricingPresentationLoaded =
    pricingPresentation?.loading === false &&
    !pricingPresentation?.error;

  const hasBillingHydrated =
    billingLoaded === true;

  const hasSettledBillingSnapshot =
    !!currentSubscription?.id &&
    !!currentSubscription?.priceId &&
    !!currentSubscription?.interval;

  const shouldHoldInteractivePricingSurface =
    !hasPricingPresentationLoaded ||
    !hasBillingHydrated;

  /**
   * Guard so we only auto-hydrate the interval from the current subscription
   * once per page load.
   *
   * Why:
   * - Existing subscribers should land on the interval they are actually on.
   * - New users with no subscription should keep the default ("annual").
   * - After the user manually changes the toggle, we should not keep forcing
   *   it back from subscription state on later renders.
   */
  const hasHydratedPricingIntervalFromSubscription = useRef(false);

  /**
   * Guard so we only auto-hydrate the seat count from the current subscription
   * once per page load.
   *
   * Why:
   * - The pricing page should initialize from the user's current subscription.
   * - After that initial hydration, the page-level seatCount becomes staged UI
   *   state owned by SubscriptionPage2.
   * - Live billing snapshot updates must NOT keep overwriting the staged seat
   *   count while the user is reviewing changes in ReviewChangesDrawer2.
   *
   * This is especially important because BillingContext can emit fresh
   * currentSubscription objects multiple times while the page is open.
   */
  const hasHydratedSeatCountFromSubscription = useRef(false);

  /**
   * Tracks whether the user has staged a pricing change locally.
   *
   * Why:
   * - SubscriptionPage2 intentionally owns staged UI state separately from the
   *   settled billing snapshot.
   * - We only want to discard staged state when the user returns from a Stripe
   *   handoff without a settled billing change.
   */
  const hasStagedBillingChangeRef = useRef(false);

  /**
   * Remembers the settled billing snapshot we launched from before the user
   * leaves for Stripe.
   *
   * Why:
   * - If the user comes back without completing payment, we need a stable
   *   reference to the last settled subscription state so the page can rehydrate
   *   cleanly instead of preserving abandoned staged values.
   */
  const checkoutLaunchSnapshotRef = useRef(null);

  /**
   * Prevents repeated reset attempts for the same Stripe return cycle.
   *
   * Why:
   * - Browser back/forward navigation and follow-up BillingContext snapshots can
   *   cause repeated renders after a Stripe handoff.
   * - We only want to perform the staged-state reset once per return cycle.
   */
  const hasHandledStripeReturnResetRef = useRef(false);

  /* ----------------------------------------------------------------
   * Session-storage helpers for staged Stripe return handling
   * ---------------------------------------------------------------- */
  const persistStripeReturnSnapshot = useCallback((snapshot) => {
    try {
      if (!snapshot) {
        sessionStorage.removeItem(STRIPE_RETURN_SNAPSHOT_KEY);
        return;
      }

      sessionStorage.setItem(
        STRIPE_RETURN_SNAPSHOT_KEY,
        JSON.stringify(snapshot),
      );
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to persist Stripe return snapshot:",
        error,
      );
    }
  }, []);

  const readStripeReturnSnapshot = useCallback(() => {
    try {
      const raw = sessionStorage.getItem(STRIPE_RETURN_SNAPSHOT_KEY);
      if (!raw) return null;

      return JSON.parse(raw);
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to read Stripe return snapshot:",
        error,
      );
      return null;
    }
  }, []);

  const setStripeReturnStagedFlag = useCallback((value) => {
    try {
      if (value) {
        sessionStorage.setItem(STRIPE_RETURN_STAGED_KEY, "true");
      } else {
        sessionStorage.removeItem(STRIPE_RETURN_STAGED_KEY);
      }
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to write staged billing flag:",
        error,
      );
    }
  }, []);

  const readStripeReturnStagedFlag = useCallback(() => {
    try {
      return sessionStorage.getItem(STRIPE_RETURN_STAGED_KEY) === "true";
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to read staged billing flag:",
        error,
      );
      return false;
    }
  }, []);

  const setStripeReturnResetHandledFlag = useCallback((value) => {
    try {
      if (value) {
        sessionStorage.setItem(STRIPE_RETURN_RESET_DONE_KEY, "true");
      } else {
        sessionStorage.removeItem(STRIPE_RETURN_RESET_DONE_KEY);
      }
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to write Stripe return reset flag:",
        error,
      );
    }
  }, []);

  const readStripeReturnResetHandledFlag = useCallback(() => {
    try {
      return (
        sessionStorage.getItem(STRIPE_RETURN_RESET_DONE_KEY) === "true"
      );
    } catch (error) {
      console.warn(
        "[SubscriptionPage2] Failed to read Stripe return reset flag:",
        error,
      );
      return false;
    }
  }, []);
  
  /* ----------------------------------------------------------------
   * Diagnostics
   * ---------------------------------------------------------------- */
  useEffect(() => {
    console.log("[SubscriptionPage2] Mounted page shell.");
  }, []);

  useEffect(() => {
    const persistedSnapshot = readStripeReturnSnapshot();
    const persistedStagedFlag = readStripeReturnStagedFlag();
    const persistedResetHandledFlag =
      readStripeReturnResetHandledFlag();

    checkoutLaunchSnapshotRef.current = persistedSnapshot;
    hasStagedBillingChangeRef.current = persistedStagedFlag;
    hasHandledStripeReturnResetRef.current =
      persistedResetHandledFlag;

    console.log(
      "[SubscriptionPage2] Hydrated Stripe return session state:",
      {
        persistedSnapshot,
        persistedStagedFlag,
        persistedResetHandledFlag,
      },
    );
  }, [
    readStripeReturnSnapshot,
    readStripeReturnStagedFlag,
    readStripeReturnResetHandledFlag,
  ]);
  
  useEffect(() => {
    console.log("[SubscriptionPage2] Received navigation state:", state);
  }, [state]);

  useEffect(() => {
    console.log("[SubscriptionPage2] Billing snapshot:", {
      billingLoaded,
      hasValidPaymentMethod,
      currentSubscription,
      hasPricingPresentationLoaded,
      hasBillingHydrated,
      hasSettledBillingSnapshot,
      shouldHoldInteractivePricingSurface,
    });
  }, [
    billingLoaded,
    hasValidPaymentMethod,
    currentSubscription,
    hasPricingPresentationLoaded,
    hasBillingHydrated,
    hasSettledBillingSnapshot,
    shouldHoldInteractivePricingSurface,
  ]);

  useEffect(() => {
    console.log("[SubscriptionPage2] Pricing interval state updated:", {
      pricingInterval,
    });
  }, [pricingInterval]);

  useEffect(() => {
    console.log("[SubscriptionPage2] Shared pricingPresentation state:", {
      loading: pricingPresentation?.loading,
      error: pricingPresentation?.error || null,
      payloadPath: pricingPresentation?.payloadPath || null,
      target: pricingPresentation?.target || null,
      pricingEnv: pricingPresentation?.pricingEnv || null,
      planKeys: Array.isArray(pricingPresentation?.plans)
        ? pricingPresentation.plans.map((plan) => plan.key)
        : [],
      compareCategories: Array.isArray(pricingPresentation?.compare)
        ? pricingPresentation.compare.map((group) => group.category)
        : [],
    });
  }, [
    pricingPresentation?.loading,
    pricingPresentation?.error,
    pricingPresentation?.payloadPath,
    pricingPresentation?.target,
    pricingPresentation?.pricingEnv,
    pricingPresentation?.plans,
    pricingPresentation?.compare,
  ]);

  useEffect(() => {
    console.log("[SubscriptionPage2] Pricing band styling active:", {
      background:
        "linear-gradient(180deg, var(--utm-heroStart, #eef2f7), var(--utm-split, #f5f5f5) 30%)",
      cardSurface: "var(--utm-card, #ffffff)",
      ink: "var(--utm-ink, #0F1A2A)",
    });
  }, []);

  useEffect(() => {
    console.log("[SubscriptionPage2] Compare section wiring active:", {
      compareCategoryCount: Array.isArray(pricingPresentation?.compare)
        ? pricingPresentation.compare.length
        : 0,
      compareCategories: Array.isArray(pricingPresentation?.compare)
        ? pricingPresentation.compare.map((group) => group.category)
        : [],
      pricingInterval,
    });
  }, [pricingPresentation?.compare, pricingInterval]);

  useEffect(() => {
    console.log("[SubscriptionPage2] FAQ section wiring active:", {
      faqStrategy: "app-local",
      faqComponent: "PricingFaq2",
    });
  }, []);

  /* ----------------------------------------------------------------
   * Sync local seatCount from current subscription
   *
   * IMPORTANT:
   * - This is intentionally a one-time hydration, not a live mirror.
   * - SubscriptionPage2 owns the staged seatCount used by:
   *     • PricingPageHeader2
   *     • PricingTable2
   *     • ReviewChangesDrawer2
   * - Once hydrated, live Firestore/billing snapshot updates should not keep
   *   overwriting the user's in-progress selection.
   * ---------------------------------------------------------------- */
useEffect(() => {
  console.log(
    "[SubscriptionPage2] currentSubscription updated:",
    currentSubscription,
  );

  if (hasHydratedSeatCountFromSubscription.current) {
    console.log(
      "[SubscriptionPage2] seatCount hydration already completed — preserving staged seatCount:",
      seatCount,
    );
    return;
  }

  if (currentSubscription) {
    if (currentSubscription.seats) {
      console.log(
        "[SubscriptionPage2] Hydrating seatCount from currentSubscription.seats:",
        currentSubscription.seats,
      );
      setSeatCount(currentSubscription.seats);
    } else {
      console.warn(
        "[SubscriptionPage2] currentSubscription.seats undefined/0 → default 1",
      );
      setSeatCount(1);
    }

    hasHydratedSeatCountFromSubscription.current = true;
    return;
  }

  console.warn(
    "[SubscriptionPage2] currentSubscription undefined — keeping existing seatCount",
  );
}, [currentSubscription]);

  /* ----------------------------------------------------------------
   * Sync pricingInterval from current subscription on first hydration
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (hasHydratedPricingIntervalFromSubscription.current) {
      return;
    }

    if (!currentSubscription?.interval) {
      return;
    }

    const rawInterval = String(currentSubscription.interval).toLowerCase().trim();

    const nextInterval =
      rawInterval === "month" || rawInterval === "monthly"
        ? "monthly"
        : rawInterval === "year" || rawInterval === "annual" || rawInterval === "yearly"
          ? "annual"
          : null;

    if (!nextInterval) {
      console.warn(
        "[SubscriptionPage2] Unable to hydrate pricingInterval from currentSubscription.interval:",
        currentSubscription.interval,
      );
      hasHydratedPricingIntervalFromSubscription.current = true;
      return;
    }

    console.log("[SubscriptionPage2] Hydrating pricingInterval from currentSubscription:", {
      subscriptionInterval: currentSubscription.interval,
      nextInterval,
    });

    setPricingInterval(nextInterval);
    hasHydratedPricingIntervalFromSubscription.current = true;
  }, [currentSubscription]);

  /* ----------------------------------------------------------------
   * Reset staged seatCount hydration when subscription identity changes
   *
   * Why:
   * - If the page is entered fresh for a different subscription context,
   *   we want the initial seatCount to hydrate correctly again.
   * - We do NOT want ordinary snapshot churn for the same subscription to
   *   keep resetting staged state.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!currentSubscription?.id) {
      return;
    }

    console.log("[SubscriptionPage2] Subscription identity available for staged state ownership:", {
      subscriptionId: currentSubscription.id,
      priceId: currentSubscription.priceId || null,
      seats: currentSubscription.seats || null,
    });
  }, [
    currentSubscription?.id,
    currentSubscription?.priceId,
    currentSubscription?.seats,
  ]);

  /* ----------------------------------------------------------------
   * Clear staged review state
   *
   * ENTERPRISE RULE
   * - SubscriptionPage2 owns staged review state.
   * - Closing or resetting the drawer should clear all page-level
   *   review selections through one centralized path.
   * ---------------------------------------------------------------- */
  const handleClearStagedReviewState = useCallback(() => {
    console.log(
      "[SubscriptionPage2] Clearing staged review state:",
      {
        action: "clear_staged_review_state",
        hadReviewContext: !!reviewContext,
        drawerOpen,
      },
    );

    setReviewContext(null);
    setDrawerOpen(false);
  }, [
    reviewContext,
    drawerOpen,
  ]);

const restoreStagedStateFromSettledSubscription = useCallback(() => {
  console.log(
    "[SubscriptionPage2] Restoring staged pricing state from settled subscription snapshot:",
    {
      currentSubscription,
      previousSeatCount: seatCount,
      previousPricingInterval: pricingInterval,
      hadReviewContext: !!reviewContext,
      drawerOpen,
    },
  );

  const settledSeatCount =
    Number.isFinite(Number(currentSubscription?.seats)) ?
      Number(currentSubscription.seats) :
      1;

  const settledInterval =
    currentSubscription?.interval === "month" ||
    currentSubscription?.interval === "monthly"
      ? "monthly"
      : currentSubscription?.interval === "year" ||
          currentSubscription?.interval === "annual" ||
          currentSubscription?.interval === "yearly"
        ? "annual"
        : "annual";

  setSeatCount(settledSeatCount);
  setPricingInterval(settledInterval);
  setReviewContext(null);
  setDrawerOpen(false);

  /* ------------------------------------------------------------
   * This is an explicit local-state restore after a confirmed
   * backend cancel/revert action. It is NOT a Stripe-return flow.
   * Clear staged Stripe-return refs so future reopen attempts
   * start from a clean state.
   * ---------------------------------------------------------- */
  checkoutLaunchSnapshotRef.current = null;
  hasStagedBillingChangeRef.current = false;
  hasHandledStripeReturnResetRef.current = true;

  persistStripeReturnSnapshot(null);
  setStripeReturnStagedFlag(false);
  setStripeReturnResetHandledFlag(true);
}, [
  currentSubscription,
  seatCount,
  pricingInterval,
  reviewContext,
  drawerOpen,
  persistStripeReturnSnapshot,
  setStripeReturnStagedFlag,
  setStripeReturnResetHandledFlag,
]);
  
  /* ----------------------------------------------------------------
   * Reset abandoned staged billing state after Stripe return
   *
   * Why:
   * - SubscriptionPage2 preserves staged seatCount / plan-selection UI while
   *   the user is reviewing or completing checkout.
   * - If the user leaves for Stripe and then returns without settling the
   *   billing change, we must discard abandoned staged state and rehydrate from
   *   the settled BillingContext snapshot.
   *
   * IMPORTANT:
   * - This is intentionally narrow.
   * - It does NOT turn seatCount back into a live mirror.
   * - It only fires when we can detect a staged pricing flow has occurred and
   *   the current settled subscription still matches the launch snapshot.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!billingLoaded) {
      return;
    }

    if (!isStripeReturnContext) {
      return;
    }

    if (!hasStagedBillingChangeRef.current) {
      return;
    }

    if (hasHandledStripeReturnResetRef.current) {
      return;
    }

    if (!checkoutLaunchSnapshotRef.current) {
      return;
    }

    if (!currentSubscription?.id) {
      return;
    }

    const launchSnapshot = checkoutLaunchSnapshotRef.current;

    const stillOnOriginalSettledSnapshot =
      launchSnapshot.subscriptionId === (currentSubscription.id || null) &&
      launchSnapshot.priceId === (currentSubscription.priceId || null) &&
      Number(launchSnapshot.seats || 0) ===
        Number(currentSubscription.seats || 0) &&
      launchSnapshot.interval === (currentSubscription.interval || null) &&
      launchSnapshot.status === (currentSubscription.status || null);

    if (!stillOnOriginalSettledSnapshot) {
      console.log(
        "[SubscriptionPage2] Stripe return reset skipped because billing snapshot changed:",
        {
          launchSnapshot,
          currentSubscription,
        },
      );
      return;
    }

    const stagedSeatCountDiffers =
      Number(seatCount || 0) !== Number(currentSubscription.seats || 0);

    const hasPendingDrawerState =
      drawerOpen ||
      !!reviewContext;

    if (!stagedSeatCountDiffers && !hasPendingDrawerState) {
      return;
    }

    console.log(
      "[SubscriptionPage2] Resetting abandoned staged billing state from settled subscription snapshot:",
      {
        launchSnapshot,
        currentSubscription,
        stagedSeatCount: seatCount,
        settledSeatCount: currentSubscription.seats || null,
        hadPendingDrawerState: hasPendingDrawerState,
      },
    );

    const settledInterval =
      currentSubscription?.interval === "month" ||
      currentSubscription?.interval === "monthly"
        ? "monthly"
        : currentSubscription?.interval === "year" ||
            currentSubscription?.interval === "annual" ||
            currentSubscription?.interval === "yearly"
          ? "annual"
          : "annual";

    setSeatCount(Number(currentSubscription.seats) || 1);
    setPricingInterval(settledInterval);
    handleClearStagedReviewState();

    hasHandledStripeReturnResetRef.current = true;
    hasStagedBillingChangeRef.current = false;

    setStripeReturnResetHandledFlag(true);
    setStripeReturnStagedFlag(false);
    persistStripeReturnSnapshot(null);
  }, [
    billingLoaded,
    isStripeReturnContext,
    currentSubscription,
    seatCount,
    drawerOpen,
    reviewContext,
    persistStripeReturnSnapshot,
    setStripeReturnStagedFlag,
    setStripeReturnResetHandledFlag,
    handleClearStagedReviewState,
  ]);

  /* ----------------------------------------------------------------
   * Clear staged-flow refs once a new settled billing snapshot lands
   *
   * Why:
   * - A successful billing change should retire the old launch snapshot and
   *   staged-state markers.
   * - This prevents a later render from mistaking a legitimate settled change
   *   for an abandoned Stripe return.
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!checkoutLaunchSnapshotRef.current) {
      return;
    }

    if (!currentSubscription?.id) {
      return;
    }

    const launchSnapshot = checkoutLaunchSnapshotRef.current;

    const billingActuallyChanged =
      launchSnapshot.subscriptionId !== (currentSubscription.id || null) ||
      launchSnapshot.priceId !== (currentSubscription.priceId || null) ||
      Number(launchSnapshot.seats || 0) !==
        Number(currentSubscription.seats || 0) ||
      launchSnapshot.interval !== (currentSubscription.interval || null) ||
      launchSnapshot.status !== (currentSubscription.status || null);

    if (!billingActuallyChanged) {
      return;
    }

    console.log(
      "[SubscriptionPage2] Settled billing snapshot changed — clearing staged-flow refs:",
      {
        launchSnapshot,
        currentSubscription,
      },
    );

    checkoutLaunchSnapshotRef.current = null;
    hasStagedBillingChangeRef.current = false;
    hasHandledStripeReturnResetRef.current = false;

    persistStripeReturnSnapshot(null);
    setStripeReturnStagedFlag(false);
    setStripeReturnResetHandledFlag(false);
  }, [
    currentSubscription,
    persistStripeReturnSnapshot,
    setStripeReturnStagedFlag,
    setStripeReturnResetHandledFlag,
  ]);

  /* ----------------------------------------------------------------
   * Banner visibility
   * ---------------------------------------------------------------- */
  const needsCardBanner =
    billingLoaded &&
    hasValidPaymentMethod === false &&
    currentSubscription?.status !== "trialing";


  /* ----------------------------------------------------------------
   * Header/table shared interval handler
   * ---------------------------------------------------------------- */
  const handlePricingIntervalChange = useCallback((nextInterval) => {
    if (nextInterval !== "annual" && nextInterval !== "monthly") {
      console.warn(
        "[SubscriptionPage2] Ignoring invalid pricing interval:",
        nextInterval,
      );
      return;
    }

    console.log("[SubscriptionPage2] handlePricingIntervalChange:", {
      previousInterval: pricingInterval,
      nextInterval,
    });

    setPricingInterval(nextInterval);
  }, [pricingInterval]);

  /* ----------------------------------------------------------------
   * Plan selection handler
   *
   * Enterprise ownership boundary:
   *   • SubscriptionPage2 owns page-level selection state
   *   • selected plan is staged into pendingPlan
   *   • drawer opens for review
   *   • ReviewChangesDrawer2 owns the confirm/runtime billing action
   *
   * This keeps page orchestration separate from billing submission logic.
   * ---------------------------------------------------------------- */
  const handlePlanSelect = useCallback(
    (plan) => {
      console.log("[SubscriptionPage2] handlePlanSelect called with:", plan);

      if (shouldHoldInteractivePricingSurface) {
        console.warn(
          "[SubscriptionPage2] Blocking plan selection because the " +
          "interactive billing surface is still hydrating.",
          {
            plan,
            billingLoaded,
            currentSubscription,
            pricingPresentationLoading: pricingPresentation?.loading,
            hasPricingPresentationLoaded,
            hasBillingHydrated,
            hasSettledBillingSnapshot,
            shouldHoldInteractivePricingSurface,
          },
        );
        return;
      }
      /* ----------------------------------------------------------
       * ENTERPRISE DRAFT-STATE RULE
       *
       * Selecting a plan is still LOCAL UI state only.
       *
       * IMPORTANT:
       * - Do NOT persist Stripe-return recovery state here.
       * - Stripe handoff state must only be created when the user
       *   actually leaves the app for Stripe.
       * - If the user has returned from an abandoned Stripe flow,
       *   a fresh local selection should clear any stale return
       *   recovery markers before opening the drawer again.
       * -------------------------------------------------------- */
      checkoutLaunchSnapshotRef.current = null;
      hasStagedBillingChangeRef.current = false;
      hasHandledStripeReturnResetRef.current = true;

      persistStripeReturnSnapshot(null);
      setStripeReturnStagedFlag(false);
      setStripeReturnResetHandledFlag(true);

      const nextReviewContext = {
        selectedPlan: plan,
        seatCount,
        currentSubscription: currentSubscription || null,
        openedAt: Date.now(),
      };

      console.log(
        "[SubscriptionPage2] Opening review drawer with reviewContext:",
        nextReviewContext,
      );

      setReviewContext(nextReviewContext);
      setDrawerOpen(true);
    },
    [
      seatCount,
      currentSubscription,
      pricingPresentation?.loading,
      billingLoaded,
      hasPricingPresentationLoaded,
      hasSettledBillingSnapshot,
      shouldHoldInteractivePricingSurface,
      persistStripeReturnSnapshot,
      setStripeReturnStagedFlag,
      setStripeReturnResetHandledFlag,
    ],
  );

  /* ----------------------------------------------------------------
   * Seat count change from PricingPageHeader2 / PricingTable2
   * ---------------------------------------------------------------- */
  const handleSeatCountChange = useCallback((newSeatCount) => {
    console.log("[SubscriptionPage2] handleSeatCountChange:", {
      previousSeatCount: seatCount,
      nextSeatCount: newSeatCount,
    });

    if (shouldHoldInteractivePricingSurface) {
      console.warn(
        "[SubscriptionPage2] Blocking seat-count change because the " +
        "interactive billing surface is still hydrating.",
        {
          attemptedSeatCount: newSeatCount,
          billingLoaded,
          currentSubscription,
          pricingPresentationLoading: pricingPresentation?.loading,
          hasPricingPresentationLoaded,
          hasBillingHydrated,
          hasSettledBillingSnapshot,
          shouldHoldInteractivePricingSurface,
        },
      );
      return;
    }

    /* ----------------------------------------------------------
     * ENTERPRISE DRAFT-STATE RULE
     *
     * Editing seat count is still LOCAL UI state only.
     *
     * IMPORTANT:
     * - Do NOT mark this as an in-flight Stripe return state.
     * - Seat edits should not re-arm abandoned-checkout recovery.
     * - A fresh local edit after returning from Stripe should
     *   clear stale return markers so the drawer can open
     *   normally for a new review cycle.
     * -------------------------------------------------------- */
    checkoutLaunchSnapshotRef.current = null;
    hasStagedBillingChangeRef.current = false;
    hasHandledStripeReturnResetRef.current = true;

    persistStripeReturnSnapshot(null);
    setStripeReturnStagedFlag(false);
    setStripeReturnResetHandledFlag(true);

    setSeatCount(Number(newSeatCount) || 1);
  }, [
    seatCount,
    pricingPresentation?.loading,
    billingLoaded,
    currentSubscription,
    hasPricingPresentationLoaded,
    hasSettledBillingSnapshot,
    shouldHoldInteractivePricingSurface,
    persistStripeReturnSnapshot,
    setStripeReturnStagedFlag,
    setStripeReturnResetHandledFlag,
  ]);

  /* ----------------------------------------------------------------
   * Stripe handoff start
   *
   * ENTERPRISE RULE
   * - Stripe-return recovery state must only be created when the
   *   user is actually leaving the app for Stripe.
   * - Local seat edits / plan selection / opening the review drawer
   *   must remain draft UI state only.
   * ---------------------------------------------------------------- */
  const handleStripeHandoffStart = useCallback(({ launchSnapshot }) => {
    console.log(
      "[SubscriptionPage2] handleStripeHandoffStart:",
      {
        launchSnapshot,
        seatCount,
        reviewContext,
        drawerOpen,
      },
    );

    checkoutLaunchSnapshotRef.current = launchSnapshot || null;
    hasStagedBillingChangeRef.current = true;
    hasHandledStripeReturnResetRef.current = false;

    persistStripeReturnSnapshot(launchSnapshot || null);
    setStripeReturnStagedFlag(true);
    setStripeReturnResetHandledFlag(false);
  }, [
    seatCount,
    reviewContext,
    drawerOpen,
    persistStripeReturnSnapshot,
    setStripeReturnStagedFlag,
    setStripeReturnResetHandledFlag,
  ]);


  /* ----------------------------------------------------------------
   * Toast close
   * ---------------------------------------------------------------- */
  const handleCloseToast = useCallback(() => {
    setToastOpen(false);
  }, []);

  /* ----------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  if (shouldHoldInteractivePricingSurface) {
    console.log(
      "[SubscriptionPage2] Holding interactive pricing surface " +
      "until billing/presentation hydration completes.",
      {
        billingLoaded,
        currentSubscription,
        pricingPresentationLoading: pricingPresentation?.loading,
        hasPricingPresentationLoaded,
        hasBillingHydrated,
        hasSettledBillingSnapshot,
        shouldHoldInteractivePricingSurface,
      },
    );

    return null;
  }

  return (
    <>
      {/* ------------------------------------------------------------
       * Pricing band
       *
       * Matches the role of .utm-pricing-band in WordPress:
       *   background: linear-gradient(180deg,var(--utm-heroStart),var(--utm-split) 30%)
       * ---------------------------------------------------------- */}
      <Box
        sx={{
          background:
            "linear-gradient(180deg, var(--utm-heroStart, #eef2f7), var(--utm-split, #f5f5f5) 30%)",
          paddingTop: 0,
          paddingBottom: "30px",
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            paddingTop: "1rem",
            paddingLeft: "2rem",
            paddingRight: "2rem",
          }}
        >
          {/* ─────────────────────────────────────────────────────────
           * Page header / intro
           * Mirrors the top-level role that pricing-intro.php serves
           * on the WordPress page.
           * ─────────────────────────────────────────────────────── */}
          <PricingPageHeader2
            context="account"
            seatCount={seatCount}
            onSeatCountChange={handleSeatCountChange}
            pricingInterval={pricingInterval}
            onIntervalChange={handlePricingIntervalChange}
            pricingPresentation={pricingPresentation}
          />

          {/* ─────────────────────────────────────────────────────────
           * Missing payment-method banner
           * Kept from your current subscription page logic.
           * ─────────────────────────────────────────────────────── */}
          <Collapse in={needsCardBanner && !bannerDismissed}>
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={redirectToCustomerPortal}
                  >
                    Add card
                  </Button>
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => setBannerDismissed(true)}
                  >
                    Dismiss
                  </Button>
                </>
              }
            >
              You don’t have a payment method on file yet. You can still review
              changes here, and Stripe may ask you to add or confirm payment
              details during the next payment step if needed.
            </Alert>
          </Collapse>

          {/* ─────────────────────────────────────────────────────────
           * New pricing card grid
           * This is the WordPress-aligned renderer path.
           * ─────────────────────────────────────────────────────── */}
          <PricingTable2
            context="account"
            seatCount={seatCount}
            onSeatCountChange={handleSeatCountChange}
            onPlanSelect={handlePlanSelect}
            pricingInterval={pricingInterval}
            onIntervalChange={handlePricingIntervalChange}
            pricingPresentation={pricingPresentation}
          />

          {/* ----------------------------------------------------------------
           * Temporary selected-plan debug summary
           *
           * Useful while migrating to the new renderer. Safe to remove later.
           * ---------------------------------------------------------------- */}
          {reviewContext?.selectedPlan && (
            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography
                variant="body2"
                sx={{ fontFamily: "Roboto, sans-serif" }}
              >
                <strong>Selected Plan:</strong>{" "}
                {reviewContext.selectedPlan.name} –{" "}
                {reviewContext.selectedPlan.pricingDisplay === "contact"
                  ? "Contact Sales"
                  : `$${reviewContext.selectedPlan.displayedSeatPrice ?? reviewContext.selectedPlan.perSeat ?? reviewContext.selectedPlan.price} / month`}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ----------------------------------------------------------------
       * Compare section
       *
       * Now wired to the same shared pricingPresentation payload used by:
       *   • PricingPageHeader2
       *   • PricingTable2
       *
       * This keeps the entire pricing page aligned to the same merged
       * source-of-truth object and avoids duplicate payload fetches.
       * ---------------------------------------------------------------- */}
      <PricingCompareTable2
        pricingPresentation={pricingPresentation}
        pricingInterval={pricingInterval}
        onPlanSelect={handlePlanSelect}
        currentSubscription={currentSubscription}
        seatCount={seatCount}
        context="account"
      />

      {/* ----------------------------------------------------------------
       * FAQ section
       *
       * This remains app-local by design for now.
       *
       * Why:
       * - pricingPresentation stays focused on structured plan/compare data
       * - marketing currently manages FAQ content in WordPress/ACF
       * - we want visual/content parity in-app without expanding the shared
       *   pricing payload schema prematurely
       * ---------------------------------------------------------------- */}
      <PricingFaq2 />

      {/* ─────────────────────────────────────────────────────────
       * New review drawer
       *
       * IMPORTANT:
       * - This aligns SubscriptionPage2 with the new WordPress-aligned
       *   pricing flow and plan objects emitted by:
       *     • PricingTable2 / PlanCard2
       *     • PricingCompareTable2
       *
       * - ReviewChangesDrawer2 owns the active review + confirm runtime path
       *   internally through BillingContext methods for:
       *     • proration preview
       *     • scheduled downgrade handling
       *     • restart handling
       *     • checkout redirect behavior
       *
       * - SubscriptionPage2 owns only the page-level review-session state that
       *   feeds the drawer:
       *     • reviewContext
       *     • seatCount
       *     • pricingInterval
       *     • drawer open / close
       * ─────────────────────────────────────────────────────── */}
      <ReviewChangesDrawer2
        open={drawerOpen}
        onClose={handleClearStagedReviewState}
        onPendingBillingCancelSuccess={restoreStagedStateFromSettledSubscription}
        reviewContext={reviewContext}
        onStripeHandoffStart={handleStripeHandoffStart}
      />

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="info"
          sx={{ width: "100%", fontFamily: "Roboto, sans-serif" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

/* ──────────────────────────────────────────────────────────────
 * Top-level wrapper
 * ─────────────────────────────────────────────────────────── */

const SubscriptionPage2 = () => {
  useEffect(() => {
    console.log("[SubscriptionPage2] Top-level page mounted.");
  }, []);

  return <ManageSubscription2 />;
};

export default SubscriptionPage2;